import {
  type CreateWorkspaceInput,
  type Workspace,
  type WorkspaceGrant,
  workspaceGrantSchema,
  workspaceSchema,
} from '@noconflict/contracts';
import type { Supabase } from '../../shared/api/supabase';

/**
 * Every workspace the caller can see, archived ones included, oldest first. RLS: a grant on the workspace,
 * an org-wide grant, or an org role that carries `projects.view`.
 */
export async function listWorkspaces(client: Supabase): Promise<Workspace[]> {
  const { data, error } = await client
    .from('workspaces')
    .select('*')
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return workspaceSchema.array().parse(data);
}

/** The caller's own workspace grants across their orgs. */
export async function listOwnGrants(client: Supabase, userId: string): Promise<WorkspaceGrant[]> {
  const { data, error } = await client.from('workspace_grants').select('*').eq('user_id', userId);
  if (error) throw new Error(error.message);
  return workspaceGrantSchema.array().parse(data);
}

/** Needs `workspaces.manage` in the org; the database allocates the slug. */
export async function createWorkspace(
  client: Supabase,
  organizationId: string,
  input: CreateWorkspaceInput,
): Promise<Workspace> {
  const { data, error } = await client.rpc('create_workspace', {
    p_organization_id: organizationId,
    p_name: input.name,
  });
  if (error) throw new Error(error.message);
  return workspaceSchema.parse(data);
}

/** Needs `workspace.manage`; refused with `workspace_archived` while the workspace is archived. */
export async function renameWorkspace(
  client: Supabase,
  workspaceId: string,
  name: string,
): Promise<void> {
  const { error } = await client.rpc('rename_workspace', {
    p_workspace_id: workspaceId,
    p_name: name,
  });
  if (error) throw new Error(error.message);
}

/** Archived workspaces are read-only and hidden by default (Q55); nothing is deleted. */
export async function setWorkspaceArchived(
  client: Supabase,
  workspaceId: string,
  archived: boolean,
): Promise<void> {
  const { error } = await client.rpc(archived ? 'archive_workspace' : 'unarchive_workspace', {
    p_workspace_id: workspaceId,
  });
  if (error) throw new Error(error.message);
}
